
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Helmet } from 'react-helmet-async';
import { PerfectBookingSkeleton } from '../templates/modern/components/PerfectBookingSkeleton';

interface BookingPageSkeletonProps {
  template?: string;
}

export const BookingPageSkeleton: React.FC<BookingPageSkeletonProps> = ({ template }) => {
  // Use Modern template skeleton if specified (now with static import)
  if (template === 'modern') {
    return <PerfectBookingSkeleton />;
  }

  return <DefaultBookingPageSkeleton />;
};

const DefaultBookingPageSkeleton = () => {
  return (
    <div className="container max-w-4xl px-4 py-12 mx-auto">
      <Helmet>
        <title>Loading Booking Page | ChronoCal</title>
        <meta name="description" content="Loading meeting booking page..." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      
      {/* Header skeleton */}
      <div className="flex flex-col items-center mb-8">
        <Skeleton className="h-12 w-12 rounded-full mb-4" />
        <Skeleton className="h-8 w-64 mb-2" />
        <Skeleton className="h-4 w-96 mb-1" />
      </div>

      <Card>
        <CardContent className="p-0 grid md:grid-cols-2">
          {/* Calendar side */}
          <div className="p-6 flex flex-col gap-4">
            <div>
              <Skeleton className="h-6 w-40 mb-2" />
              <Skeleton className="h-4 w-64" />
            </div>
            
            <div className="space-y-2">
              <div className="grid grid-cols-7 gap-1">
                {Array(7).fill(0).map((_, i) => (
                  <Skeleton key={`day-${i}`} className="h-8 w-full" />
                ))}
              </div>
              {Array(5).fill(0).map((_, row) => (
                <div key={`week-${row}`} className="grid grid-cols-7 gap-1">
                  {Array(7).fill(0).map((_, col) => (
                    <Skeleton key={`cell-${row}-${col}`} className="h-10 w-full" />
                  ))}
                </div>
              ))}
            </div>
            
            <Skeleton className="h-10 w-full mt-2" />
          </div>
          
          {/* Time slots side */}
          <div className="border-t md:border-l md:border-t-0 p-6">
            <div className="mb-4">
              <Skeleton className="h-6 w-36 mb-2" />
              <Skeleton className="h-4 w-64" />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {Array(8).fill(0).map((_, i) => (
                <Skeleton key={`slot-${i}`} className="h-10 w-full" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const CardSkeleton = () => {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Calendar loading component
export const CalendarLoading = () => {
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex flex-col items-center mb-8">
        <Skeleton className="h-12 w-12 rounded-full mb-4" />
        <Skeleton className="h-8 w-64 mb-2" />
        <Skeleton className="h-4 w-96 mb-1" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-6 w-40 mb-4" />
            <div className="space-y-2">
              <div className="grid grid-cols-7 gap-1">
                {Array(7).fill(0).map((_, i) => (
                  <Skeleton key={`day-${i}`} className="h-8 w-full" />
                ))}
              </div>
              {Array(5).fill(0).map((_, row) => (
                <div key={`week-${row}`} className="grid grid-cols-7 gap-1">
                  {Array(7).fill(0).map((_, col) => (
                    <Skeleton key={`cell-${row}-${col}`} className="h-10 w-full" />
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-6 w-36 mb-4" />
            <div className="grid grid-cols-2 gap-2">
              {Array(8).fill(0).map((_, i) => (
                <Skeleton key={`slot-${i}`} className="h-10 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Export LoadingStates object for backwards compatibility
export const LoadingStates = {
  Card: CardSkeleton,
  CalendarLoading: CalendarLoading,
};
